import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setDeliveryAddress } from "../../store/cart/cart.action";
import { selectDeliveryAddress } from "../../store/cart/cart.selector";
import {
  selectKitchen,
  selectOnlineOrdersConfigs,
} from "../../store/kitchen/kitchen.selector";
import { getColor } from "../../utils/utils.common";
import { googleMapsApiKey } from "../../utils/firebase/configKeys";
import {
  geocodeAddress,
  isWithinMaxDeliveryDistance,
  LatLng,
  latLngFromGoogleLatLng,
} from "../../utils/deliveryDistance";
import { getMaxDeliveryDistanceKm } from "../../utils/deliveryConfig";
import {
  deliveryAddressFromPlaceComponents,
  formatDeliveryAddress,
  isDeliveryAddressSet,
  DeliveryAddress,
} from "../../utils/deliveryAddress";
import { fieldError, successColor } from "../../constants";
import { SuccessIcon } from "../icons";
import styled from "styled-components";

declare global {
  interface Window {
    google?: any;
    __googlePlacesScriptPromise?: Promise<void>;
  }
}

const MIN_AUTOCOMPLETE_CHARS = 6;
const AUTOCOMPLETE_DEBOUNCE_MS = 300;

const loadGoogleMapsScript = (apiKey: string): Promise<void> => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Window is not available"));
  }

  if (window.google?.maps?.importLibrary) {
    return Promise.resolve();
  }

  if (window.__googlePlacesScriptPromise) {
    return window.__googlePlacesScriptPromise;
  }

  window.__googlePlacesScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[data-google-places="true"]'
    ) as HTMLScriptElement | null;

    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load Google Maps script"))
      );
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.googlePlaces = "true";
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);
  });

  return window.__googlePlacesScriptPromise;
};

const FieldContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const Label = styled.label<{ textBody?: string }>`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 6px;
  color: ${(props) => props.textBody || "#344054"};

  @media screen and (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

const Input = styled.input<{
  fieldBorder?: string;
  fieldBackground?: string;
  fieldFilledText?: string;
  fieldPlaceholderText?: string;
  brandPrimary?: string;
  hasError?: boolean;
}>`
  display: block;
  width: 100%;
  box-sizing: border-box;
  height: 48px;
  font-size: 18px;
  padding: ${(props) => (props.hasError ? "4px 36px 4px 14px" : "4px 14px")};
  font-weight: 400;
  border-radius: 8px;
  background: ${(props) => props.fieldBackground || "#FFFFFF"};
  border: 1.5px solid
    ${(props) => (props.hasError ? fieldError : props.fieldBorder || "#D0D5DD")};
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  color: ${(props) => props.fieldFilledText || "#344054"};

  &::placeholder {
    color: ${(props) => props.fieldPlaceholderText || "#98A2B3"};
    font-size: 18px;
    font-weight: 400;
  }

  &:focus {
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05),
      0px 0px 0px 4px rgba(238, 238, 238, 1);
    outline: none;
    border-color: ${(props) =>
      props.hasError ? fieldError : props.brandPrimary || "#6C01CC"};
    background: ${(props) => props.fieldBackground || "#FFFFFF"};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media screen and (max-width: 768px) {
    height: 44px;
    font-size: 16px;

    &::placeholder {
      font-size: 16px;
    }
  }
`;

const SuggestionsList = styled.ul<{
  fieldBorder?: string;
  fieldBackground?: string;
  headerModalBackground?: string;
}>`
  list-style: none;
  margin: 4px 0 0;
  padding: 4px 0;
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% - 0px);
  z-index: 20;
  max-height: 220px;
  overflow-y: auto;
  background: ${(props) =>
    props.headerModalBackground || props.fieldBackground || "#FFFFFF"};
  border: 1.5px solid ${(props) => props.fieldBorder || "#D0D5DD"};
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(16, 24, 40, 0.12);
`;

const SuggestionItem = styled.li<{
  textBody?: string;
  brandPrimary?: string;
  backgroundTertiary?: string;
}>`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.textBody || "#344054"};

  &:hover,
  &:focus {
    background: ${(props) => props.backgroundTertiary || "#F2F4F7"};
    color: ${(props) => props.brandPrimary || "#6C01CC"};
  }
`;

const HintText = styled.div<{ textBody?: string }>`
  margin-top: 6px;
  font-size: 12px;
  line-height: 18px;
  color: ${(props) => props.textBody || "#344054"};
`;

const SuccessText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 14px;
  line-height: 20px;
  color: ${successColor};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const ErrorText = styled.div`
  margin-top: 6px;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${fieldError};

  @media screen and (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`;

type SuggestionOption = {
  id: string;
  label: string;
  placePrediction: any;
};

type DeliveryAddressAutocompleteProps = {
  error?: string;
  onReady?: () => void;
  active?: boolean;
};

const DeliveryAddressAutocomplete = ({
  error: externalError,
  onReady,
  active = true,
}: DeliveryAddressAutocompleteProps) => {
  const dispatch = useDispatch();
  const deliveryAddress = useSelector(selectDeliveryAddress);
  const onlineOrdersConfig = useSelector(selectOnlineOrdersConfigs);
  const kitchen = useSelector(selectKitchen);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const restaurantLatLngRef = useRef<LatLng | null>(null);
  const sessionTokenRef = useRef<any>(null);
  const requestIdRef = useRef(0);
  const debounceTimerRef = useRef<number | null>(null);
  const placesLibRef = useRef<any>(null);
  const placesReadyPromiseRef = useRef<Promise<any> | null>(null);
  const inputValueRef = useRef(formatDeliveryAddress(deliveryAddress) || "");
  const deliveryAddressRef = useRef(deliveryAddress);

  const [inputValue, setInputValue] = useState(
    formatDeliveryAddress(deliveryAddress) || ""
  );
  const [suggestions, setSuggestions] = useState<SuggestionOption[]>([]);
  const [distanceError, setDistanceError] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isPlacesReady, setIsPlacesReady] = useState(false);

  const textBody = getColor("text-body", onlineOrdersConfig);
  const fieldBorder = getColor("field-border", onlineOrdersConfig);
  const fieldBackground = getColor("field-background", onlineOrdersConfig);
  const fieldFilledText = getColor("field-filled-text", onlineOrdersConfig);
  const fieldPlaceholderText = getColor(
    "field-placeholder-text",
    onlineOrdersConfig
  );
  const brandPrimary = getColor("brand-primary", onlineOrdersConfig);
  const backgroundTertiary = getColor("background-tertiary", onlineOrdersConfig);
  const headerModalBackground = getColor(
    "header-modal-background",
    onlineOrdersConfig
  );

  const maxDistanceKm = getMaxDeliveryDistanceKm(kitchen);
  const displayError = distanceError || externalError || "";
  const trimmedInputLength = inputValue.trim().length;
  const showMinCharsHint =
    trimmedInputLength > 0 &&
    trimmedInputLength < MIN_AUTOCOMPLETE_CHARS &&
    !isDeliveryAddressSet(deliveryAddress);
  const showLoadingPlacesHint =
    !isDeliveryAddressSet(deliveryAddress) &&
    trimmedInputLength >= MIN_AUTOCOMPLETE_CHARS &&
    (!isPlacesReady || isLoadingSuggestions) &&
    suggestions.length === 0;
  const showWithinRangeSuccess =
    isDeliveryAddressSet(deliveryAddress) && !displayError && !isValidating;

  const ensureSessionToken = (AutocompleteSessionToken: any) => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const resetSessionToken = () => {
    sessionTokenRef.current = null;
  };

  const ensurePlacesLibrary = (): Promise<any> => {
    if (placesLibRef.current) {
      return Promise.resolve(placesLibRef.current);
    }

    if (!googleMapsApiKey) {
      return Promise.reject(new Error("googleMapsApiKey is missing"));
    }

    if (!placesReadyPromiseRef.current) {
      placesReadyPromiseRef.current = (async () => {
        await loadGoogleMapsScript(googleMapsApiKey);
        if (!window.google?.maps?.importLibrary) {
          throw new Error("Google Maps importLibrary is unavailable");
        }
        const placesLib = await window.google.maps.importLibrary("places");
        placesLibRef.current = placesLib;
        // Create the first session token as soon as the library is ready.
        if (placesLib?.AutocompleteSessionToken) {
          ensureSessionToken(placesLib.AutocompleteSessionToken);
        }
        return placesLib;
      })().catch((error) => {
        placesReadyPromiseRef.current = null;
        throw error;
      });
    }

    return placesReadyPromiseRef.current;
  };

  const getRestaurantLatLng = async (): Promise<LatLng | null> => {
    if (restaurantLatLngRef.current) {
      return restaurantLatLngRef.current;
    }
    if (!kitchen?.fullAddress) {
      return null;
    }
    const coords = await geocodeAddress(kitchen.fullAddress);
    if (coords) {
      restaurantLatLngRef.current = coords;
    }
    return coords;
  };

  const validateAndSetAddress = async (
    address: DeliveryAddress | null,
    deliveryLatLng: LatLng | null
  ) => {
    if (!address || !isDeliveryAddressSet(address)) {
      setDistanceError("");
      dispatch(setDeliveryAddress(null));
      return;
    }

    if (!deliveryLatLng) {
      setDistanceError("Please select an address from the suggestions.");
      dispatch(setDeliveryAddress(null));
      return;
    }

    setIsValidating(true);
    try {
      const restaurantLatLng = await getRestaurantLatLng();
      if (!restaurantLatLng) {
        setDistanceError(
          "Unable to verify delivery distance. Please try again."
        );
        dispatch(setDeliveryAddress(null));
        return;
      }

      const withinRange = isWithinMaxDeliveryDistance(
        restaurantLatLng,
        deliveryLatLng,
        maxDistanceKm
      );

      if (!withinRange) {
        setDistanceError(
          `This address is outside the delivery area (max ${maxDistanceKm} km).`
        );
        dispatch(setDeliveryAddress(null));
        return;
      }

      setDistanceError("");
      dispatch(setDeliveryAddress(address));
      setSuggestions([]);
    } finally {
      setIsValidating(false);
    }
  };

  useEffect(() => {
    restaurantLatLngRef.current = null;
  }, [kitchen?.fullAddress]);

  useEffect(() => {
    deliveryAddressRef.current = deliveryAddress;
    if (isDeliveryAddressSet(deliveryAddress)) {
      const formatted = formatDeliveryAddress(deliveryAddress);
      setInputValue(formatted);
      inputValueRef.current = formatted;
    }
  }, [deliveryAddress]);

  useEffect(() => {
    let cancelled = false;

    const prepare = async () => {
      if (!active) {
        setSuggestions([]);
        setIsPlacesReady(false);
        return;
      }

      if (!googleMapsApiKey) {
        console.warn(
          "googleMapsApiKey is missing from configKeys; Places autocomplete disabled."
        );
        return;
      }

      try {
        await ensurePlacesLibrary();
        if (cancelled) {
          return;
        }

        setIsPlacesReady(true);

        if (kitchen?.fullAddress) {
          const coords = await geocodeAddress(kitchen.fullAddress);
          if (!cancelled && coords) {
            restaurantLatLngRef.current = coords;
          }
        }

        if (!cancelled) {
          onReady?.();
          // If the user already typed enough while Places was loading, fetch now.
          // Skip when the input is just the already-validated address prefilled
          // from the store (e.g. reopening the sheet), so the suggestions
          // dropdown doesn't pop open on its own.
          const pendingQuery = inputValueRef.current.trim();
          if (
            pendingQuery.length >= MIN_AUTOCOMPLETE_CHARS &&
            !isDeliveryAddressSet(deliveryAddressRef.current)
          ) {
            fetchSuggestions(pendingQuery);
          }
        }
      } catch (err) {
        console.error("Failed to initialize Google Places autocomplete:", err);
        if (!cancelled) {
          setIsPlacesReady(false);
        }
      }
    };

    prepare();

    return () => {
      cancelled = true;
      if (debounceTimerRef.current) {
        window.clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, kitchen?.fullAddress]);

  const fetchSuggestions = async (query: string) => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_AUTOCOMPLETE_CHARS) {
      setSuggestions([]);
      setIsLoadingSuggestions(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoadingSuggestions(true);

    try {
      // Wait for Places + session token on first open instead of dropping the request.
      const placesLib = await ensurePlacesLibrary();
      if (requestId !== requestIdRef.current) {
        return;
      }

      const { AutocompleteSuggestion, AutocompleteSessionToken } = placesLib;
      const sessionToken = ensureSessionToken(AutocompleteSessionToken);
      // Address-only: street/building results (no businesses/POIs).
      const { suggestions: nextSuggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: trimmed,
          includedPrimaryTypes: ["street_address", "premise", "subpremise"],
          includedRegionCodes: ["au"],
          sessionToken,
        });

      if (requestId !== requestIdRef.current) {
        return;
      }

      const blockedTypes = new Set([
        "establishment",
        "point_of_interest",
        "food",
        "store",
        "restaurant",
        "cafe",
      ]);

      const options: SuggestionOption[] = (nextSuggestions || [])
        .map((suggestion: any, index: number) => {
          // Skip query predictions / non-place results.
          if (suggestion?.queryPrediction || !suggestion?.placePrediction) {
            return null;
          }

          const placePrediction = suggestion.placePrediction;
          const types: string[] =
            placePrediction.types ||
            placePrediction.placeTypes ||
            [];
          if (types.some((type) => blockedTypes.has(type))) {
            return null;
          }

          const label =
            placePrediction.text?.text ||
            placePrediction.toString?.() ||
            trimmed;
          return {
            id: `${label}-${index}`,
            label,
            placePrediction,
          };
        })
        .filter(Boolean);

      setSuggestions(options);
      setIsPlacesReady(true);
    } catch (err) {
      console.error("Failed to fetch address suggestions:", err);
      if (requestId === requestIdRef.current) {
        setSuggestions([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingSuggestions(false);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
    inputValueRef.current = value;
    setDistanceError("");
    dispatch(setDeliveryAddress(null));

    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
    }

    if (value.trim().length < MIN_AUTOCOMPLETE_CHARS) {
      setSuggestions([]);
      setIsLoadingSuggestions(false);
      return;
    }

    // Kick off library load immediately on first qualifying keystroke.
    ensurePlacesLibrary().catch(() => {
      /* logged in fetchSuggestions if the request still proceeds */
    });

    debounceTimerRef.current = window.setTimeout(() => {
      fetchSuggestions(value);
    }, AUTOCOMPLETE_DEBOUNCE_MS);
  };

  const handleSelectSuggestion = async (option: SuggestionOption) => {
    setIsValidating(true);
    setSuggestions([]);
    try {
      const place = option.placePrediction.toPlace();
      await place.fetchFields({
        fields: ["formattedAddress", "displayName", "location", "addressComponents"],
      });
      resetSessionToken();

      const formatted =
        place.formattedAddress || place.displayName || option.label;
      const structured = deliveryAddressFromPlaceComponents(
        place.addressComponents,
        formatted
      );

      if (!structured) {
        setDistanceError("Unable to parse delivery address. Please try again.");
        dispatch(setDeliveryAddress(null));
        return;
      }

      setInputValue(formatDeliveryAddress(structured));
      const deliveryLatLng = latLngFromGoogleLatLng(place.location);
      await validateAndSetAddress(structured, deliveryLatLng);
    } catch (err) {
      console.error("Failed to resolve selected place:", err);
      setDistanceError("Unable to verify delivery address. Please try again.");
      dispatch(setDeliveryAddress(null));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <FieldContainer>
      <Label textBody={textBody}>Delivery Address</Label>
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        placeholder="Start typing your address"
        onChange={handleInputChange}
        onBlur={() => {
          // Keep suggestions briefly so click selection can register.
          window.setTimeout(() => setSuggestions([]), 150);
          const value = inputValue.trim();
          if (value && !isDeliveryAddressSet(deliveryAddress)) {
            setDistanceError("Please select an address from the suggestions.");
          }
        }}
        fieldBorder={fieldBorder}
        fieldBackground={fieldBackground}
        fieldFilledText={fieldFilledText}
        fieldPlaceholderText={fieldPlaceholderText}
        brandPrimary={brandPrimary}
        hasError={!!displayError}
        autoComplete="off"
        disabled={isValidating || !active}
      />
      {suggestions.length > 0 && (
        <SuggestionsList
          fieldBorder={fieldBorder}
          fieldBackground={fieldBackground}
          headerModalBackground={headerModalBackground}
        >
          {suggestions.map((option) => (
            <SuggestionItem
              key={option.id}
              textBody={textBody}
              brandPrimary={brandPrimary}
              backgroundTertiary={backgroundTertiary}
              onMouseDown={(event) => {
                event.preventDefault();
                handleSelectSuggestion(option);
              }}
            >
              {option.label}
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
      {showMinCharsHint ? (
        <HintText textBody={textBody}>
          Keep typing — suggestions appear after {MIN_AUTOCOMPLETE_CHARS}{" "}
          characters
        </HintText>
      ) : null}
      {showLoadingPlacesHint ? (
        <HintText textBody={textBody}>
          Loading address suggestions…
        </HintText>
      ) : null}
      {displayError ? <ErrorText>{displayError}</ErrorText> : null}
      {showWithinRangeSuccess ? (
        <SuccessText>
          <SuccessIcon />
          Address within delivery range
        </SuccessText>
      ) : null}
    </FieldContainer>
  );
};

export default DeliveryAddressAutocomplete;
